import { MessageTemplate as ConfigMessageTemplate, MessageType } from '../types';

export interface MessageTypeOption {
  value: MessageType;
  title: string;
}

export interface MessageFieldVisibility {
  /** The SMS/MMS toggle. */
  showTypeToggle: boolean;
  /** Which of the two the toggle offers. */
  typeOptions: MessageTypeOption[];
  showAttachment: boolean;
  showSubject: boolean;
  showText: boolean;
}

interface MessageVisibilityInput {
  messageType: MessageType;
  /** The config template governing this message, when the config defines one. */
  template?: ConfigMessageTemplate;
  /** Types the config allows, e.g. ['sms','mms']. */
  allowedMessageTypes: string[];
  /** 1-based. */
  position: number;
  isReminder?: boolean;
  isFlyer?: boolean;
}

/**
 * Which fields the message editor shows for one message.
 *
 * A config message template wins outright when there is one; only configs with
 * no templates fall back to the allowedMessageTypes heuristics. Deriving these
 * from allowedMessageTypes alone hid the SMS/MMS toggle on every template-driven
 * config, because those leave `message.types` empty.
 */
export function resolveMessageFieldVisibility({
  messageType,
  template,
  allowedMessageTypes,
  position,
  isReminder = false,
  isFlyer = false,
}: MessageVisibilityInput): MessageFieldVisibility {
  // The flyer's first message IS the flyer: an attachment with no copy.
  const isFirstFlyerMessage = isFlyer && position === 1;
  const isMms = messageType === 'mms';
  const templateType = template?.messageType;
  const fields = template?.mmsFieldsVisibility;

  const offersSms = template
    ? templateType !== 'mms-only'
    : allowedMessageTypes.includes('sms') || !isFirstFlyerMessage;
  const offersMms = template
    ? templateType !== 'sms-only'
    : allowedMessageTypes.includes('mms');

  return {
    showTypeToggle: template
      ? templateType === 'sms-or-mms'
      : (isFlyer && position > 1)
        || ((!isReminder || position > 1) && allowedMessageTypes.length > 1),

    typeOptions: [
      ...(offersSms ? [{ value: 'sms' as MessageType, title: 'SMS' }] : []),
      ...(offersMms ? [{ value: 'mms' as MessageType, title: 'MMS' }] : []),
    ],

    showAttachment: template
      ? !!fields?.attachment && (isMms || isFirstFlyerMessage)
      : isMms || isFirstFlyerMessage,

    showSubject: isMms && (template ? !!fields?.subject : !isFirstFlyerMessage),

    showText: template ? !!fields?.text : !isFirstFlyerMessage,
  };
}
